// Synergy progress — "where am I with each tribe, and what gets me to the
// next rung".
//
// Pure arithmetic over card arrays, kept out of the components so it can be
// unit tested (the project runs vitest under `node`, with no jsdom).
//
// The one rule that shapes everything here: breakpoints are HIGHEST-ONLY,
// not cumulative. Reaching 4 REPLACES the 2 payoff. Both the active and the
// next breakpoint come from the same resolvers the engine uses
// (getActiveBreakpoint / getNextBreakpoint in game/data/tribes.ts), so the
// UI cannot describe a payoff combat wouldn't actually apply.
//
// Fielded vs. potential is a hard distinction: only the board counts toward
// a real synergy. Hand and shop are projections and must never be folded
// into the number the player reads as their count.

import type { Card, TribeId } from '../game/data/cards/types';
import {
  TRIBE_DEFINITIONS,
  getActiveBreakpoint,
  getNextBreakpoint,
  type TribeDefinition,
  type TribeSynergyBreakpoint,
} from '../game/data/tribes';
import { countTribesOnBoard } from '../game/tribeSystem';

export interface TribeProgress {
  tribe: TribeDefinition;
  /** Units of this tribe actually fielded. The only number the engine sees. */
  count: number;
  /** Units of this tribe sitting in hand — playable this turn. */
  fromHand: number;
  /** Units of this tribe currently buyable in the shop. */
  fromShop: number;
  /** Highest breakpoint currently met, or null below the first. */
  active: TribeSynergyBreakpoint | null;
  /** Next unreached breakpoint, or null once the top rung is met. */
  next: TribeSynergyBreakpoint | null;
  /** Units still needed to reach `next`. 0 when `next` is null. */
  toNext: number;
  /** Could playing everything in hand reach the next rung this turn? */
  reachableFromHand: boolean;
}

/** Count one tribe across an arbitrary pile of cards (hand, shop, board). */
export function countTribeIn(cards: (Card | null)[], tribe: TribeId): number {
  return countTribesOnBoard(cards).get(tribe) ?? 0;
}

export function progressFor(
  tribe: TribeDefinition,
  board: (Card | null)[],
  hand: (Card | null)[] = [],
  shop: (Card | null)[] = [],
): TribeProgress {
  const count = countTribeIn(board, tribe.id);
  const fromHand = countTribeIn(hand, tribe.id);
  const fromShop = countTribeIn(shop, tribe.id);
  const active = getActiveBreakpoint(tribe, count);
  const next = getNextBreakpoint(tribe, count);
  const toNext = next ? next.threshold - count : 0;

  return {
    tribe,
    count,
    fromHand,
    fromShop,
    active,
    next,
    toNext,
    reachableFromHand: next !== null && fromHand >= toNext,
  };
}

/** Progress for every tribe in the registry, in registry order. */
export function allTribeProgress(
  board: (Card | null)[],
  hand: (Card | null)[] = [],
  shop: (Card | null)[] = [],
  tribes: TribeDefinition[] = TRIBE_DEFINITIONS,
): TribeProgress[] {
  return tribes.map((t) => progressFor(t, board, hand, shop));
}

/**
 * Ordering for the inline chip row.
 *
 *   1. Tribes with an active breakpoint first, highest threshold first —
 *      what's already paying out matters most.
 *   2. Then by fewest units needed to reach the next rung, so the tribe
 *      you're closest to completing surfaces above one you've barely
 *      started.
 *   3. Ties broken by count, then name, so the row never reshuffles on an
 *      unrelated re-render.
 */
export function orderTribesForRow(entries: TribeProgress[]): TribeProgress[] {
  return [...entries].sort((a, b) => {
    const aActive = a.active?.threshold ?? 0;
    const bActive = b.active?.threshold ?? 0;
    if (aActive !== bActive) return bActive - aActive;

    // null `next` means maxed — treat as closest so it stays at the top of
    // its band rather than sinking below unstarted tribes.
    const aToNext = a.next === null ? -1 : a.toNext;
    const bToNext = b.next === null ? -1 : b.toNext;
    if (aToNext !== bToNext) return aToNext - bToNext;

    if (a.count !== b.count) return b.count - a.count;
    return a.tribe.name.localeCompare(b.tribe.name);
  });
}

/** Tribes the player has actually started — the inline row's filter. */
export function startedTribes(entries: TribeProgress[], minCount = 1): TribeProgress[] {
  return entries.filter((e) => e.count >= minCount);
}

export interface CappedRow {
  shown: TribeProgress[];
  /** How many were dropped. Surfaced as a "+N" affordance, never silently. */
  overflowCount: number;
}

/** Cap the row so a 390px header can't be swamped by six chips. */
export function capRow(entries: TribeProgress[], limit: number): CappedRow {
  if (limit <= 0) return { shown: [], overflowCount: entries.length };
  if (entries.length <= limit) return { shown: entries, overflowCount: 0 };
  return {
    shown: entries.slice(0, limit),
    overflowCount: entries.length - limit,
  };
}
